import { Controller } from '@hotwired/stimulus'
import { driver } from 'driver.js'

export default class extends Controller {
  static values = {
    settingsPath: String,
    tourPath: String,
    locale: String
  }

  get t() {
    const locale = this.localeValue || 'en'
    return TRANSLATIONS[locale] || TRANSLATIONS.en
  }

  connect() {
    if (!this.isOnSettingsPage()) {
      Turbo.visit(this.settingsPathValue)
      return
    }

    if (document.querySelector('.driver-active') || window.__operfTourRunning) {
      return
    }

    // Small delay to let the page fully render
    requestAnimationFrame(() => this.startTour())
  }

  isOnSettingsPage() {
    return window.location.pathname.includes('/settings')
  }

  startTour() {
    const t = this.t
    const driverObj = driver({
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: t.nextBtn,
      prevBtnText: t.prevBtn,
      doneBtnText: t.doneBtn,
      popoverClass: 'operf-tour',
      animate: true,
      overlayColor: 'rgb(0 0 0)',
      overlayOpacity: 0.5,
      onDestroyStarted: () => {
        if (!driverObj.hasNextStep() || confirm(t.skipConfirm)) {
          driverObj.destroy()
          this.completeTour()
        }
      },
      onDestroyed: () => {
        this.removeClickBlocker()
        this.completeTour()
      },
      steps: this.buildSteps()
    })

    window.__operfTourRunning = true
    this.addClickBlocker()
    driverObj.drive()
  }

  addClickBlocker() {
    this._clickBlocker = (event) => {
      const blocked = event.target.closest([
        '#positions_list .btn-warning.btn-square',
        '#positions_list .btn-error.btn-square',
        '#positions_settings .btn-success',
        '#boolean_settings button',
        '#boolean_settings a',
        '#tour-players-link',
        '#tour-settings-link',
        '#tour-new-player-link'
      ].join(', '))
      if (blocked) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
    }
    document.addEventListener('click', this._clickBlocker, true)
  }

  removeClickBlocker() {
    if (this._clickBlocker) {
      document.removeEventListener('click', this._clickBlocker, true)
      this._clickBlocker = null
    }
  }

  buildSteps() {
    const t = this.t
    const steps = [
      {
        popover: {
          title: t.steps.welcome.title,
          description: t.steps.welcome.description,
          side: 'center',
          align: 'center'
        }
      },
      {
        element: '#positions_settings',
        popover: {
          title: t.steps.positions.title,
          description: t.steps.positions.description,
          side: 'right',
          align: 'start'
        }
      }
    ]

    const sortHandleSel = '#positions_list .sort-handle'
    if (document.querySelector(sortHandleSel)) {
      steps.push({
        element: sortHandleSel,
        popover: {
          title: t.steps.reorder.title,
          description: t.steps.reorder.description,
          side: 'right',
          align: 'start'
        }
      })
    }

    const defaultBtnSel = '#positions_list .btn-soft.btn-xs:not(.btn-square):not(.btn-primary)'
    if (document.querySelector(defaultBtnSel)) {
      steps.push({
        element: defaultBtnSel,
        popover: {
          title: t.steps.setDefault.title,
          description: t.steps.setDefault.description,
          side: 'left',
          align: 'center'
        }
      })
    }

    const editBtnSel = '#positions_list .btn-warning.btn-square'
    if (document.querySelector(editBtnSel)) {
      steps.push({
        element: editBtnSel,
        popover: {
          title: t.steps.edit.title,
          description: t.steps.edit.description,
          side: 'left',
          align: 'center'
        }
      })
    }

    const deleteBtnSel = '#positions_list .btn-error.btn-square'
    if (document.querySelector(deleteBtnSel)) {
      steps.push({
        element: deleteBtnSel,
        popover: {
          title: t.steps.delete.title,
          description: t.steps.delete.description,
          side: 'left',
          align: 'center'
        }
      })
    }

    const addBtnSel = '#positions_settings .btn-success'
    if (document.querySelector(addBtnSel)) {
      steps.push({
        element: addBtnSel,
        popover: {
          title: t.steps.addNew.title,
          description: t.steps.addNew.description,
          side: 'top',
          align: 'center'
        }
      })
    }

    steps.push({
      element: '#tour-settings-grid',
      popover: {
        title: t.steps.allSettings.title,
        description: t.steps.allSettings.description,
        side: 'top',
        align: 'center'
      }
    })

    const booleanCard = document.querySelector('#boolean_settings')
    if (booleanCard) {
      steps.push({
        element: booleanCard,
        popover: {
          title: t.steps.booleanSettings.title,
          description: t.steps.booleanSettings.description,
          side: 'left',
          align: 'start'
        }
      })
    }

    const playersLink = document.querySelector('#tour-players-link')
    if (playersLink) {
      steps.push({
        element: playersLink,
        popover: {
          title: t.steps.players.title,
          description: t.steps.players.description,
          side: 'right',
          align: 'center'
        }
      })
    }

    const settingsLink = document.querySelector('#tour-settings-link')
    if (settingsLink) {
      const dropdownEl = settingsLink.closest('[data-controller~="shared--dropdown"]')
      const getDropdownController = () => dropdownEl
        ? this.application.getControllerForElementAndIdentifier(dropdownEl, 'shared--dropdown')
        : null

      steps.push({
        element: settingsLink,
        popover: {
          title: t.steps.settingsLink.title,
          description: t.steps.settingsLink.description,
          side: 'right',
          align: 'center'
        },
        onHighlightStarted: () => {
          getDropdownController()?.open()
        },
        onDeselected: () => {
          getDropdownController()?.close()
        }
      })
    }

    const newPlayerLink = document.querySelector('#tour-new-player-link')
    if (newPlayerLink) {
      steps.push({
        element: newPlayerLink,
        popover: {
          title: t.steps.newPlayer.title,
          description: t.steps.newPlayer.description,
          side: 'right',
          align: 'center'
        }
      })
    }

    return steps
  }

  completeTour() {
    if (this._tourCompleted) return
    this._tourCompleted = true
    window.__operfTourRunning = false

    fetch(this.tourPathValue, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      }
    })

    // Remove the controller from DOM so it doesn't restart on Turbo visits
    this.element.removeAttribute('data-controller')
    this.element.removeAttribute('data-tour-settings-path-value')
    this.element.removeAttribute('data-tour-tour-path-value')
  }
}

const TRANSLATIONS = {
  en: {
    nextBtn: 'Next →',
    prevBtn: '← Back',
    doneBtn: "Let's go!",
    skipConfirm: 'Are you sure you want to skip the tour?',
    steps: {
      welcome: {
        title: '👋 Welcome to Operf!',
        description: "Your account is ready. Let's take 2 minutes together to discover the main features — starting with your settings."
      },
      positions: {
        title: '⚙️ Settings made for your club',
        description: 'Everything in Operf can be adapted to the way your club works. <strong>Positions</strong> is a good example — you can rename, reorder or remove any of them to match your needs.'
      },
      reorder: {
        title: '↕️ Change the order',
        description: 'Drag this handle to move items up or down. The new order will show up everywhere in the app — on player cards, filters and dropdown menus.'
      },
      setDefault: {
        title: '⭐ Set a default choice',
        description: 'Click <strong>Default</strong> to pre-select an option in player forms. Only one default can be active at a time, so pick the one your staff uses the most — it saves them a click every time.'
      },
      edit: {
        title: '✏️ Rename',
        description: 'Click here to rename an item. The new name is updated instantly everywhere in the app.'
      },
      delete: {
        title: '🗑️ Remove',
        description: "Delete items you don't use. Don't worry — players already linked to a removed item will stay untouched."
      },
      addNew: {
        title: '➕ Add your own',
        description: 'Type a name and click <strong>Add</strong> to create a new item. You can add as many as you want — make Operf truly yours.'
      },
      allSettings: {
        title: '🗂️ Same logic everywhere',
        description: 'Positions, Profiles, Statuses, Contracts, Priorities, Levels... all of them work exactly the same way. Taking a few minutes now to set them up will make managing your squad much easier later on.'
      },
      booleanSettings: {
        title: '✅ Yes / No options',
        description: 'Some player details are simply yes or no — like <strong>Work ethic</strong>, <strong>Experience</strong> or <strong>Injury prone</strong>. Here you can choose which Yes/No fields show up on player profiles, set their default value, and even create your own to fit your scouting habits.'
      },
      players: {
        title: '🏀 Your players',
        description: 'Once your settings are ready, go to <strong>Players</strong> to view and manage your whole squad. You can search, filter and add scouting notes from there.'
      },
      settingsLink: {
        title: '⚙️ Come back anytime',
        description: 'You can always come back to this page using the <strong>Settings</strong> link in the menu — feel free to update your lists as your club grows and changes.'
      },
      newPlayer: {
        title: '🚀 Add your first player',
        description: "You're all set! Click <strong>New player</strong> to start building your squad. Everything you just set up will be ready to use in the player form."
      }
    }
  },
  fr: {
    nextBtn: 'Suivant →',
    prevBtn: '← Retour',
    doneBtn: "C'est parti !",
    skipConfirm: 'Êtes-vous sûr de vouloir quitter la visite ?',
    steps: {
      welcome: {
        title: '👋 Bienvenue sur Operf !',
        description: "Votre compte est prêt. Prenons 2 minutes ensemble pour découvrir les fonctionnalités principales — on commence par vos paramètres."
      },
      positions: {
        title: '⚙️ Des paramètres adaptés à votre club',
        description: "Tout dans Operf peut être adapté à la façon dont votre club fonctionne. Les <strong>Postes</strong> en sont un bon exemple : vous pouvez les renommer, les réorganiser ou les supprimer pour coller à vos habitudes."
      },
      reorder: {
        title: '↕️ Changer l\'ordre',
        description: "Attrapez cette poignée pour déplacer un élément vers le haut ou vers le bas. Le nouvel ordre s'applique partout dans l'application : fiches joueurs, filtres et menus déroulants."
      },
      setDefault: {
        title: '⭐ Définir un choix par défaut',
        description: "Cliquez sur <strong>Défaut</strong> pour pré-sélectionner une option dans les formulaires joueurs. Une seule option peut être définie par défaut à la fois — choisissez celle que votre staff utilise le plus souvent, ça lui fera gagner un clic à chaque fois."
      },
      edit: {
        title: '✏️ Renommer',
        description: "Cliquez ici pour renommer un élément. Le nouveau nom est mis à jour instantanément partout dans l'application."
      },
      delete: {
        title: '🗑️ Supprimer',
        description: "Supprimez les éléments que vous n'utilisez pas. Pas d'inquiétude : les joueurs déjà associés à un élément supprimé ne seront pas modifiés."
      },
      addNew: {
        title: '➕ Ajouter les vôtres',
        description: "Tapez un nom puis cliquez sur <strong>Ajouter</strong> pour créer un nouvel élément. Il n'y a aucune limite — personnalisez Operf à votre image."
      },
      allSettings: {
        title: '🗂️ La même logique partout',
        description: "Postes, Profils, Statuts, Contrats, Priorités, Niveaux... tous fonctionnent exactement de la même manière. Prendre quelques minutes maintenant pour les configurer vous simplifiera beaucoup la gestion de votre effectif par la suite."
      },
      booleanSettings: {
        title: '✅ Les options Oui / Non',
        description: "Certaines informations sur les joueurs sont simplement oui ou non — comme <strong>Éthique de travail</strong>, <strong>Expérience</strong> ou <strong>Sujet aux blessures</strong>. Vous pouvez choisir ici quels champs Oui/Non apparaissent sur les fiches joueurs, définir leur valeur par défaut, et même créer les vôtres selon vos habitudes de scouting."
      },
      players: {
        title: '🏀 Vos joueurs',
        description: "Une fois vos paramètres prêts, rendez-vous dans <strong>Joueurs</strong> pour consulter et gérer tout votre effectif. Vous pourrez y faire des recherches, filtrer et ajouter vos notes de scouting."
      },
      settingsLink: {
        title: '⚙️ Revenir quand vous voulez',
        description: "Vous pouvez revenir sur cette page à tout moment via le lien <strong>Paramètres</strong> dans le menu — n'hésitez pas à mettre vos listes à jour au fur et à mesure que votre club évolue."
      },
      newPlayer: {
        title: '🚀 Ajouter votre premier joueur',
        description: "Tout est prêt ! Cliquez sur <strong>Nouveau joueur</strong> pour commencer à constituer votre effectif. Tous les paramètres que vous venez de régler seront déjà disponibles dans le formulaire."
      }
    }
  }
}
